/**
 * libs
 */
import type { IClientSubscribeOptions, IClientSubscribeProperties } from "mqtt";

/**
 * interfaces
 */
import { IProfileConfig } from "../../interfaces/profile/profile-config";

/**
 * models
 */
import { ProfileHandler } from "./profile-handler";
import { TopicRegistry } from "../topic-management/topic-registry";

/**
 * Central registry for managing ProfileHandler instances.
 * @class ProfileRegistry
 * @description
 * - Manages the lifecycle of application profiles (feature groups).
 * - Loads initial profiles from configuration.
 * - Ensures unique profile names per connection.
 */
export class ProfileRegistry {
    
    /**
     * Internal map of active profiles.
     * Key: ProfileId (string), Value: ProfileHandler instance.
     * @private
     */
    private readonly profiles: Map<string, ProfileHandler> = new Map<string, ProfileHandler>();

    /**
     * Returns an array of all active ProfileHandler instances.
     * @public
     */
    public get profilesArray(): ProfileHandler[] {
        return Array.from(this.profiles.values());
    }

    /**
     * Creates an instance of ProfileRegistry.
     * @constructor
     * @param {string} connectionId - The ID of the connection this registry belongs to.
     * @param {TopicRegistry} topicRegistry - The shared topic registry used by profiles.
     * @param {IProfileConfig[]} [_initProfiles=[]] - Optional list of initial profiles to load.
     * @throws {Error} If arguments are invalid.
     */
    constructor(
        public readonly connectionId: string,
        public readonly topicRegistry: TopicRegistry,
        private readonly _initProfiles: IProfileConfig[] = [],
    ) {
        
        // global validation
        if (typeof this.connectionId !== 'string' || this.connectionId.length === 0) {
            throw new Error('Connection ID must be a string & non-empty');
        }
        if (!(this.topicRegistry instanceof TopicRegistry)) {
            throw new Error('Topic registry must be an instance of TopicRegistry');
        }

        // If there are initial profiles, add them to the profile registry
        if (Array.isArray(this._initProfiles) && this._initProfiles.length > 0) {
            this._initProfiles.forEach(profile => {
                
                // Note: This logic seems slightly flawed in the original code as it creates a handler but uses 'profile.options' which is required but might be missing in IProfileConfig if not strict.
                // Assuming IProfileConfig matches the creation needs.
                const profileHandler = this.createOrGetProfile(profile.name, profile.options);

                this.add(profile.name, profileHandler);
            });
        }

    }

    /**
     * Retrieves an existing ProfileHandler by name.
     * @public
     * @param {string} name - The profile name.
     * @returns {ProfileHandler | undefined} The handler or undefined.
     */
    public profile(name: string): ProfileHandler | undefined {
        const profileId = this._generateProfileId(name, this.connectionId);
        return this.profiles.get(profileId);
    }

    /**
     * Retrieves an existing profile or creates a new one.
     * @public
     * @param {string} name - The profile name.
     * @param {IClientSubscribeOptions | IClientSubscribeProperties} options - Default subscription options for this profile.
     * @returns {ProfileHandler} The active profile handler.
     */
    public createOrGetProfile(name: string, options: IClientSubscribeOptions | IClientSubscribeProperties): ProfileHandler {

        // global validation
        this._exceptionOnInvalidProfileFilter(name, this.connectionId, options);

        const profileId = this._generateProfileId(name, this.connectionId);

        // If the profile already exists, return the existing profile handler
        if (this.profiles.has(profileId)) {
            return this.profiles.get(profileId)!;
        }

        // If the profile does not exist, create a new profile handler
        const profileHandler = new ProfileHandler(name, this.connectionId, this.topicRegistry, options);
        this.profiles.set(profileId, profileHandler);

        return profileHandler;
    }

    /**
     * Manually adds a ProfileHandler instance.
     * @public
     * @param {string} name - The profile name.
     * @param {ProfileHandler} profile - The handler instance.
     */
    public add(name: string, profile: ProfileHandler): void {
        // global validation
        this._exceptionOnInvalidProfileFilter(name, this.connectionId, profile.options);

        const profileId = this._generateProfileId(name, this.connectionId);
        this.profiles.set(profileId, profile);
    }

    /**
     * Removes a profile from the registry.
     * @public
     * @param {string} name - The profile name.
     */
    public remove(name: string): void {
        const profileId = this._generateProfileId(name, this.connectionId);
        this.profiles.delete(profileId);
    }

    /**
     * Generates the unique profile ID.
     * @private
     */
    private _generateProfileId(name: string, connectionId: string): string {
        return `${connectionId}:${name}`;
    }

    /**
     * Returns a string representation.
     * @public
     */
    public toString(): string {
        return `ProfileRegistry(profiles: ${this.profiles.size})`;
    }

    /**
     * Validates profile creation parameters.
     * @private
     */
    private _exceptionOnInvalidProfileFilter(name: string, connectionId: string, options: IClientSubscribeOptions | IClientSubscribeProperties): void {
        if (name == null) {
            throw new Error('Name must be a non-empty string');
        }
        else if (typeof name !== 'string' || name.length === 0) {
            throw new Error('Name must be a string & non-empty');
        }
        else if (typeof connectionId !== 'string' || connectionId.length === 0) {
            throw new Error('Connection ID must be a string & non-empty');
        }
        else if (typeof options !== 'object' && Object.keys(options).length === 0) {
            throw new Error('Options must be a non-empty object');
        }
    }

    /**
     * Clears all registered profiles.
     * @public
     */
    public destroy(): void {
        this.profiles.clear();
    }

}
